import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import moment from "moment";
import type { ReactNode } from "react";
import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { SearchInput } from "@freightsimple/ui-core/Controls/SearchInput";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Switch } from "@freightsimple/ui-core/Controls/Switch";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { T } from "@freightsimple/ui-core/Typography/T";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import PageTitle from "@/Shared/Layout/PageTitle";
import { formatSignedPrice } from "@/Helpers/formatSignedPrice";
import { groupBy } from "@/Helpers/groupBy";
import { useOnce } from "@/Hooks/useOnce";
import { useShipmentService } from "@/Services/ShipmentService";
import { useInvoicesApi } from "@/Hooks/useInvoicesApi";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import { BranchFilterOptionType } from "@/Shared/Types/BranchFilterOptionType";
import {
  UserSelectType,
  useDashboardUserSelect,
} from "@/Features/Dashboard/Exports/Hooks/useDashboardUserSelect";
import { paymentsPayableInvoices } from "@/Features/Payments/Exports/Helpers/paymentsPayableInvoices";
import { QuotesGetInstantQuotesButton } from "@/Shared/Quotes/Buttons/QuotesGetInstantQuotesButton";
import { InvoicesCreditBanner } from "./CustomComponents/InvoicesCreditBanner";
import InvoicesSection from "./CustomComponents/InvoicesSection";
import type { InvoicesTableRow } from "./Tables/InvoicesTable";
import { InvoicesTable } from "./Tables/InvoicesTable";
import { InvoicesNoMatchesState } from "./Internal/invoicesNoMatchesState";
import {
  invoicesIsIssued,
  invoicesIsUnpaid,
  invoicesTotalDisputed,
  invoicesTotalOpenRepeatsAnotherTile,
  invoicesTotalOverdue,
  invoicesTotalPending,
  invoicesTotalOpen,
} from "./Helpers/invoicesTotals";
import { invoicesSingleCurrency } from "./Helpers/invoicesSingleCurrency";
import { invoicesBillingMonthDate } from "./Helpers/invoicesBillingMonthDate";
import { invoicesTotalSpend } from "./Exports/Helpers/invoicesTotalSpend";
import { useInvoicesStats } from "@/Shared/Invoices/Hooks/useInvoicesStats";

interface StatisticProps {
  label: ReactNode;
  children: number;
  hideIf?: boolean | undefined;
  danger?: boolean | undefined;
}

function Statistic_(props: StatisticProps) {
  if (props.hideIf) {
    return <></>;
  }

  // Rounded to cents before comparing, like formatSignedPrice below. These totals subtract
  // refunds now, so a month whose charges were exactly refunded lands on floating-point residue
  // (-1.42e-14) rather than 0 - which rendered a "$0.00" tile instead of hiding it.
  if (Math.round(props.children * 100) === 0) {
    return <></>;
  }

  return (
    <Stack gap={0} align="start">
      <T size="sm" color="muted">
        {props.label}
      </T>
      <T color={props.danger ? "red" : "default"}>
        {formatSignedPrice(props.children)}
      </T>
    </Stack>
  );
}

interface OnlyShowUnpaidInvoicesSwitchProps {
  on: boolean;
  onChange: (newValue: boolean) => void;
}

function OnlyShowUnpaidInvoicesSwitch_(
  props: OnlyShowUnpaidInvoicesSwitchProps,
) {
  return (
    <Row gap={2}>
      <T size="sm" weight="normal" color="muted" nowrap>
        Hide paid invoices
      </T>
      <Switch checked={props.on} onChange={props.onChange} />
    </Row>
  );
}

function ViewInvoicesScreen() {
  const shipmentsService = useShipmentService();
  const [invoices, setInvoices] = useState<Array<Invoice>>([]);
  const { invoicesStats, refresh: refreshInvoicesStats } = useInvoicesStats();

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [bookedShipments, setBookedShipments] = useState<Array<Shipment>>([]);
  const invoicesApi = useInvoicesApi();
  const navigate = useNavigate();

  const {
    element: UserSelectElement,
    filterByUserId,
    filterByBranchId,
  } = useDashboardUserSelect(UserSelectType.Booking);

  const [onlyShowUnpaidInvoices, setOnlyShowUnpaidInvoices] = useState(false);

  async function loadInvoices() {
    const response = await invoicesApi.listInvoices();
    setInvoices(response.invoices);
    setOnlyShowUnpaidInvoices(
      response.invoices.some((invoice) => invoicesIsUnpaid(invoice)),
    );
  }

  // Background variant for refreshes triggered from inside an open dialog
  // (Make Payments). It must NOT flip `loading`: that swaps the whole screen
  // for <PageLoadingSpinner/>, which unmounts the dialog host mid-flow — the
  // payment success stage would vanish before the user sees it.
  async function refreshInBackground() {
    await Promise.all([
      loadInvoices(),
      loadShipments(),
      refreshInvoicesStats(),
    ]);
  }

  async function refresh() {
    setLoading(true);
    setLoadFailed(false);
    try {
      await refreshInBackground();
    } catch (e) {
      console.warn("Error loading invoices:", { e });
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }

  useOnce(async () => {
    await refresh();
  });

  const shipmentsById = new Map(
    bookedShipments.map((shipment) => [shipment.shipmentId, shipment]),
  );

  // Every money figure on this screen is summed over this set. An invoice whose shipment isn't in
  // the list this screen loaded can't render a row - tableRowsForMonth drops it - so counting it
  // anywhere would put money on the page that no row below accounts for. The tiles and the banner
  // read it directly; the month headers read it through filterInvoices(), so all three agree.
  const invoicesOnScreen = invoices.filter((invoice) =>
    shipmentsById.has(invoice.shipmentId),
  );

  function applyFilters(_invoices: Invoice[]) {
    let filteredInvoices = _invoices;

    if (onlyShowUnpaidInvoices) {
      filteredInvoices = filteredInvoices.filter(invoicesIsUnpaid);
    }
    if (filter) {
      filteredInvoices = filteredInvoices.filter(function (invoice) {
        return invoice.invoiceIdentifier
          .toUpperCase()
          .includes(filter.toUpperCase());
      });
    }

    if (filterByUserId !== undefined && filterByUserId !== "") {
      filteredInvoices = filteredInvoices.filter(function (invoice) {
        return invoice.bookedBy === filterByUserId;
      });
    }

    if (filterByBranchId !== undefined && filterByBranchId !== "") {
      filteredInvoices = filteredInvoices.filter(function (invoice) {
        if (filterByBranchId === BranchFilterOptionType.Uncoded) {
          return invoice.branchId === undefined;
        }
        return invoice.branchId === filterByBranchId;
      });
    }

    return filteredInvoices;
  }

  function filterInvoices() {
    return applyFilters(invoicesOnScreen);
  }

  const hasInvoicesNeedingPayment = invoicesOnScreen.some(invoicesIsIssued);

  // Whether to offer the pay button is the dialog's own question, asked with
  // the dialog's own predicate — "unpaid" also covers refunds, which it won't
  // list. Deliberately the whole account, not filterInvoices(): searching for
  // an invoice number or picking a user narrows what's on screen, and it used
  // to take the button away with it.
  const showMakeAPaymentButton =
    paymentsPayableInvoices(invoices, bookedShipments).length > 0;

  const showUncodedOption = invoices.some((s) => s.branchId === undefined);

  const nothingToShow = filterInvoices().length === 0;
  const monthFormat = "MMMM YYYY";
  const groupedInvoices = groupBy(filterInvoices(), function (invoice) {
    const m = moment(invoicesBillingMonthDate(invoice, shipmentsById));
    return m.format(monthFormat);
  }).sort(function (d1, d2) {
    // Descending - newer invoices should be at the top
    return (
      moment(d2.key, monthFormat).valueOf() -
      moment(d1.key, monthFormat).valueOf()
    );
  });

  async function loadShipments() {
    const response = await shipmentsService.getShipments();

    setBookedShipments(response.booked);
  }

  if (loading) {
    return <PageLoadingSpinner />;
  }

  // refresh() waits for the stats too, and useInvoicesStats only logs its own
  // failures, so stats still missing once loading is done means they failed
  if (loadFailed || invoicesStats === undefined) {
    return (
      <>
        <PageTitle>Invoices</PageTitle>
        <FullWidthLayout
          header="Invoices"
          content={undefined}
          onRetryFailedLoad={refresh}
        />
      </>
    );
  }

  function tableRowsForMonth(monthInvoices: Invoice[]): InvoicesTableRow[] {
    return (
      applyFilters(monthInvoices)
        // Within each month, invoices should be sorted newest first
        .sort(function (d1, d2) {
          return (
            moment(d2.invoiceDate).valueOf() - moment(d1.invoiceDate).valueOf()
          );
        })
        .flatMap(function (invoice): InvoicesTableRow[] {
          const shipment = shipmentsById.get(invoice.shipmentId);

          // invoicesOnScreen already dropped the invoices with no shipment, so this can't happen -
          // it's here to narrow the type.
          if (shipment === undefined) {
            return [];
          }

          const shipmentId = shipment.shipmentId;

          return [
            {
              invoice,
              shipment,
              onViewShipment: function () {
                navigate(`/view-shipment?shipmentId=${shipmentId}`);
              },
              onDownloadInvoice: function () {
                return shipmentsService.downloadInvoice(
                  shipmentId,
                  invoice.invoiceIdentifier,
                  invoice.invoicePdfKey,
                );
              },
            },
          ];
        })
    );
  }

  return (
    <>
      <PageTitle>Invoices</PageTitle>
      <FullWidthLayout
        header="Invoices"
        microcopy="Here you can quickly browse through all of your FreightSimple invoices"
        showEmptyState={bookedShipments.length === 0}
        rightContent={
          <Row gap={6} wrap>
            <Statistic_
              label="Overdue"
              danger
              hideIf={!hasInvoicesNeedingPayment}
            >
              {invoicesTotalOverdue(invoicesOnScreen)}
            </Statistic_>
            {/* Its own tile because the row marker alone left a customer with
                a red overdue figure they couldn't account for. */}
            <Statistic_ label="In dispute" hideIf={!hasInvoicesNeedingPayment}>
              {invoicesTotalDisputed(invoicesOnScreen)}
            </Statistic_>
            <Statistic_
              label="Total open"
              hideIf={
                !hasInvoicesNeedingPayment ||
                invoicesTotalOpenRepeatsAnotherTile(invoicesOnScreen)
              }
            >
              {invoicesTotalOpen(invoicesOnScreen)}
            </Statistic_>
            <Statistic_ label="Payment Sent">
              {invoicesTotalPending(invoicesOnScreen)}
            </Statistic_>
            <OnlyShowUnpaidInvoicesSwitch_
              on={onlyShowUnpaidInvoices}
              onChange={setOnlyShowUnpaidInvoices}
            />
            <SearchInput
              value={filter ?? ""}
              onChange={function (newValue) {
                setFilter(newValue === "" ? undefined : newValue);
              }}
              placeholder="Enter Invoice Number"
              tooltip="Search for a shipment"
              width="md"
              data-testid="invoice-search-input"
            />
            {UserSelectElement(showUncodedOption)}
          </Row>
        }
        emptyState={
          <Stack gap={8} align="center">
            <Stack gap={0} align="center">
              {/* Replica of the legacy Cargo Heading1 (28px bold). */}
              <Heading level={1}>You don&apos;t have any invoices yet</Heading>
              <T size="base" color="secondary">
                Once you have booked shipments, you will be able to quickly
                browse all invoices here.
              </T>
            </Stack>
            <QuotesGetInstantQuotesButton />
          </Stack>
        }
        content={
          <>
            <InvoicesCreditBanner
              shipments={bookedShipments}
              invoices={invoicesOnScreen}
              paymentTermsDays={invoicesStats.paymentTermsDays}
              creditAmount={invoicesStats.creditAmount}
              creditCurrency={invoicesStats.creditCurrency}
              issuedInvoicesTotal={invoicesStats.issuedInvoicesTotal}
              issuedInvoicesChargesTotal={
                invoicesStats.issuedInvoicesChargesTotal
              }
              invoiceWarningState={invoicesStats.invoiceWarningState}
              bookingBlocked={invoicesStats.bookingBlocked}
              overdueIssuedInvoicesTotal={
                invoicesStats.overdueIssuedInvoicesTotal
              }
              showMakeAPaymentButton={showMakeAPaymentButton}
              refresh={refreshInBackground}
            />
            {nothingToShow && <InvoicesNoMatchesState />}
            {groupedInvoices.map(function (monthOfInvoices) {
              const formattedMonth = monthOfInvoices.key;
              const totalCost = invoicesTotalSpend(monthOfInvoices.value);
              // Basically group by shipment, then count
              const shipmentCount = groupBy(
                monthOfInvoices.value,
                (i) => i.shipmentId,
              ).length;
              return (
                <Fragment key={formattedMonth}>
                  <InvoicesSection
                    sectionTitle={formattedMonth}
                    totalCost={totalCost}
                    currency={invoicesSingleCurrency(monthOfInvoices.value)}
                    shipmentCount={shipmentCount}
                  />
                  <InvoicesTable
                    rows={tableRowsForMonth(monthOfInvoices.value)}
                  />
                </Fragment>
              );
            })}
          </>
        }
      />
    </>
  );
}

export default ViewInvoicesScreen;
